import { useMemo, useCallback, useState } from 'react';
import { Button } from '@antimatterhq/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@antimatterhq/ui/components/dropdownMenu';
import { ChevronDown, Clock } from 'lucide-react';
import { cn } from '@antimatterhq/ui/lib/utils';
import { getTimezoneAbbreviation } from '@antimatterhq/ui/utils/dateUtils';
import { now, ZonedDateTime, toTimeZone } from '@internationalized/date';
import { useGenericFuzzyFiltering } from '@antimatterhq/ui/hooks/fuzzyFiltering/useGenericFuzzyFiltering';
import { COMMON_TIMEZONES } from '@antimatterhq/ui/constants/timezone';
import { Input } from '../input/input';

type IntlWithZones = typeof Intl & {
  supportedValuesOf?: (key: string) => string[];
};

const i18nIntl = Intl as IntlWithZones;

const ALL_IANA_ZONES: string[] =
  typeof i18nIntl.supportedValuesOf === 'function'
    ? i18nIntl.supportedValuesOf('timeZone')
    : [...COMMON_TIMEZONES];

const COMMON_TZ_SET: Set<string> = new Set(COMMON_TIMEZONES);

interface TimezonePickerProps {
  value: string;
  onChange: (timezone: string) => void;
  className?: string;
  currentDateRange?: { start: ZonedDateTime; end: ZonedDateTime } | null;
}

export const TimezonePicker = ({
  value,
  onChange,
  className,
  currentDateRange,
}: TimezonePickerProps) => {
  // Get current system timezone for proper fallback
  const systemTimezone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone,
    [],
  );

  // Search term state for filtering
  const [filter, setFilter] = useState('');

  // Build timezone options with current DST-aware data
  const rawOptions = useMemo(() => {
    const localTimezone = systemTimezone;
    const timezones = ALL_IANA_ZONES.map((tz) => ({
      label:
        tz === 'Etc/UTC'
          ? 'UTC'
          : tz.split('/').pop()?.replace(/_/g, ' ') || tz,
      value: tz as string,
    }));

    // Ensure we always have a canonical "UTC" option at +0
    if (!timezones.some((tz) => tz.value === 'UTC')) {
      timezones.unshift({ label: 'UTC', value: 'UTC' as string });
    }

    // Check if the user's local timezone is already in the common list
    const isLocalInCommonList = timezones.some(
      (tz) => tz.value === localTimezone,
    );

    // If local timezone is not in the common list, add it
    if (!isLocalInCommonList) {
      const localLabel =
        localTimezone.split('/').pop()?.replace(/_/g, ' ') || localTimezone;
      timezones.unshift({
        label: localLabel,
        value: localTimezone as string,
      });
    }

    // Add current DST-aware data to each timezone
    return timezones.map((tz) => {
      const isLocal = tz.value === localTimezone;
      const abbreviation = getTimezoneAbbreviation(tz.value);
      const offsetHours = now(tz.value).offset / 3_600_000; // ms → hours (fractional permitted)

      return {
        label: isLocal ? `${tz.label} (Local)` : tz.label,
        value: tz.value,
        offsetHours,
        abbreviation,
        isLocal,
        isUTC: tz.value === 'UTC' || tz.value === 'Etc/UTC',
        isCommon: COMMON_TZ_SET.has(tz.value),
      };
    });
  }, [systemTimezone]);

  // Run fuzzy filtering across key fields
  const filteredOptions = useGenericFuzzyFiltering(rawOptions, filter, [
    { name: 'label', weight: 0.5 },
    { name: 'value', weight: 0.25 },
    { name: 'abbreviation', weight: 0.25 },
  ]);

  // Apply ordering to the filtered list
  const timezoneOptions = useMemo(() => {
    const local = filteredOptions.find((o) => o.isLocal);

    // Treat any variant of UTC ("UTC" or "Etc/UTC") as UTC entry
    const utc = filteredOptions.find((o) => o.isUTC);

    const selected = filteredOptions.find(
      (o) => o.value === value && o !== local && o !== utc,
    );

    // Build popular list following COMMON_TIMEZONES order but without duplicates
    const popular: typeof filteredOptions = [];
    COMMON_TIMEZONES.forEach((tzId) => {
      if (tzId === 'UTC') return; // handled separately
      const opt = filteredOptions.find((o) => o.value === tzId);
      if (opt && opt !== local && opt !== utc && opt !== selected) {
        popular.push(opt);
      }
    });

    // Remaining time zones (excluding duplicates / popular)
    const rest = filteredOptions.filter(
      (o) => o !== local && o !== utc && o !== selected && !popular.includes(o),
    );

    // Sort the remainder by offset ascending (UTC-12 ... UTC+14)
    rest.sort((a, b) => a.offsetHours - b.offsetHours);

    return [
      ...(local ? [local] : []),
      ...(utc ? [utc] : []),
      ...(selected ? [selected] : []),
      ...popular,
      ...rest,
    ];
  }, [filteredOptions, value]);

  // Determine separator positions: after UTC and after last popular item
  const separatorIndices = useMemo(() => {
    const indices: Set<number> = new Set();

    const utcIdx = timezoneOptions.findIndex((o) => o.isUTC);
    if (utcIdx !== -1 && utcIdx < timezoneOptions.length - 1) {
      indices.add(utcIdx);
    }

    // Last popular zone index
    let lastPopularIdx = -1;
    for (let i = timezoneOptions.length - 1; i >= 0; i -= 1) {
      const opt = timezoneOptions[i];
      if (opt.isCommon && !(opt.isUTC || opt.isLocal)) {
        lastPopularIdx = i;
        break;
      }
    }
    if (lastPopularIdx !== -1 && lastPopularIdx < timezoneOptions.length - 1) {
      indices.add(lastPopularIdx);
    }

    return indices;
  }, [timezoneOptions]);

  // Memoize the selected timezone calculation with proper fallback logic.
  // Use `rawOptions` so the selection remains visible even when it is
  // filtered out of the dropdown list by the search term.
  const selectedTimezone = useMemo(() => {
    const exactMatch = rawOptions.find((tz) => tz.value === value);
    if (exactMatch) return exactMatch;

    // If `value` is empty we prefer UTC by default
    if (!value) {
      const utcMatch = rawOptions.find(
        (tz) => tz.value === 'UTC' || tz.value === 'Etc/UTC',
      );
      if (utcMatch) return utcMatch;
    }

    // Fallback chain: UTC -> local timezone -> first option
    return (
      rawOptions.find((tz) => tz.value === 'UTC' || tz.value === 'Etc/UTC') ||
      rawOptions.find((tz) => tz.isLocal) ||
      rawOptions[0]
    );
  }, [value, rawOptions]);

  // Calculate tooltip content showing alternative timezone
  const tooltipContent = useMemo(() => {
    if (!currentDateRange) return null;

    // Determine alternative timezone
    const isCurrentUTC = value === 'UTC';
    const alternativeTimezone = isCurrentUTC ? systemTimezone : 'UTC';

    // Format date range in alternative timezone
    const formatOptions: Intl.DateTimeFormatOptions = {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: alternativeTimezone,
    };

    // Convert ZonedDateTime to the alternative timezone for display
    const startInAltTz = toTimeZone(
      currentDateRange.start,
      alternativeTimezone,
    );
    const endInAltTz = toTimeZone(currentDateRange.end, alternativeTimezone);

    // Format using native Intl.DateTimeFormat with toDate()
    const formatter = new Intl.DateTimeFormat('en-US', formatOptions);
    const startStr = formatter.format(startInAltTz.toDate());
    const endStr = formatter.format(endInAltTz.toDate());

    const alternativeLabel = isCurrentUTC ? 'Local Time' : 'UTC';
    return `${alternativeLabel}: ${startStr} - ${endStr}`;
  }, [currentDateRange, value, systemTimezone]);

  // Memoize event handlers
  const handleTimezoneClick = useCallback(
    (timezone: string, e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(timezone);
    },
    [onChange],
  );

  const handleTriggerClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  // Memoize the offset formatting function
  const formatOffset = useCallback((offsetHours: number): string => {
    const sign = offsetHours >= 0 ? '+' : '-';
    const hours = Math.floor(Math.abs(offsetHours));
    const minutes = Math.round((Math.abs(offsetHours) - hours) * 60);

    if (minutes === 0) {
      return `UTC${sign}${hours}`;
    }

    return `UTC${sign}${hours}:${minutes.toString().padStart(2, '0')}`;
  }, []);

  const timezoneButton = useMemo(
    () => (
      <Button
        variant="ghost"
        size="small"
        className="h-4 gap-1 px-2 text-xs"
        title={tooltipContent || undefined}
      >
        <Clock className="h-3 w-3" />
        <span>{selectedTimezone.abbreviation || 'UTC'}</span>
        <ChevronDown className="h-3 w-3" />
      </Button>
    ),
    [selectedTimezone, tooltipContent],
  );

  return (
    <div className={className} onClick={handleTriggerClick}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>{timezoneButton}</DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="h-full w-72 p-2">
          <Input
            type="text"
            placeholder="Search…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
            className="w-full rounded border px-2 py-1 text-sm"
          />
          <div className="mt-2 h-full max-h-64 overflow-y-auto overflow-x-hidden custom-scrollbar">
            {timezoneOptions.flatMap((timezone, idx) => {
              const isSelected = timezone.value === value;

              const elements = [
                <DropdownMenuItem
                  key={timezone.value}
                  onClick={(e) => handleTimezoneClick(timezone.value, e)}
                  className={cn(
                    'grid w-full grid-cols-[auto_1fr] items-center justify-between',
                    isSelected && 'bg-blue-100 dark:bg-blue-900',
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="truncate">
                      {timezone.label}{' '}
                      <span className="text-gray-500">
                        ({timezone.abbreviation})
                      </span>
                    </span>
                  </div>

                  <span className="text-right text-gray-400">
                    {formatOffset(timezone.offsetHours)}
                  </span>
                </DropdownMenuItem>,
              ];

              // Insert separators after UTC and last popular groups
              if (separatorIndices.has(idx)) {
                elements.push(
                  <DropdownMenuSeparator
                    key={`sep-${idx}`}
                    className="my-1 bg-gray-200 dark:bg-gray-800"
                  />,
                );
              }

              return elements;
            })}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
